<template>
  <div>
    <top-menu @logout="$emit('logout')" />
    <DetailPageHeader
      :user-id="userId"
      :display-name="displayName"
      :is-withdrawn="userProfile.withdrawn"
      :withdrawn-at="userProfile.withdrawnAt"
      :is-required-status="isRequiredStatus"
      :is-watch-status="isWatchStatus"
      :latest-karte-url="latestConsultationKarteUrl"
      @save-status="handleChangeStatus"
      @open-latest-karte="handleOpenLatestKarte"
    />
    <div
      v-if="isLoading"
      class="face-sheet-loading ma-6"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <v-progress-circular
        indeterminate
        color="#3ACAC9"
        size="32"
        aria-label="読み込み中"
      />
      <span class="face-sheet-loading__text">読み込み中です…</span>
    </div>
    <v-alert
      v-else-if="!faceSheet"
      type="warning"
      variant="tonal"
      class="ma-6"
    >
      ID:{{ userId }} のユーザーが見つかりません。
    </v-alert>
    <v-alert
      v-else-if="!detailData"
      type="warning"
      variant="tonal"
      class="ma-6"
    >
      ID:{{ userId }} の詳細データが見つかりません。
    </v-alert>
    <template v-else>
      <v-container
        fluid
        class="pa-0"
      >
        <UserInfoCard
          :user-id="userId"
          :display-name="displayName"
          :user-profile="userProfile"
          :user-birthday-with-age="userBirthdayWithAge"
          :formatted-postal-code="formattedPostalCode"
          :coupon-name="faceSheet.couponName"
        >
          <UserSpecialNoteSection
            v-model:special-note-draft="specialNoteDraft"
            :is-editing="isEditingSpecialNote"
            :is-saving="isDetailSaving"
            :history="specialNoteHistory"
            @edit="startEditSpecialNote"
            @save="saveSpecialNote"
            @cancel="cancelEditSpecialNote"
          />
        </UserInfoCard>
      </v-container>

      <v-container
        fluid
        class="pa-0 detail-table-list"
      >
        <ChildrenInfoTable
          :children="children"
          @open-detail="handleOpenChildDetail"
        />
        <ChildrenInfoDetailWindow
          v-if="isChildDetailDialogOpen"
          :child="activeChild"
          :note-history="activeChildNoteHistory"
          @cancel="closeChildDetailDialog"
        />
        <ExternalLinksTable
          :external-links="externalLinks"
          @add-link="handleAddExternalLink"
          @open-detail="handleOpenExternalLinkDetail"
        />
        <EventTimelineTable
          :events="events"
          @add-event="handleAddEvent"
          @open-detail="handleOpenTimelineEventDetail"
        />
        <HighRiskAlertSupportTable
          :high-risk-alert-supports="highRiskAlertSupports"
          @open-detail="handleOpenHighRiskAlertSupportDetail"
        />
        <FrequentSupportTable
          :frequent-supports="frequentSupports"
          @add-support="handleAddFrequentSupport"
          @open-detail="handleOpenFrequentSupportDetail"
        />
        <MunicipalityCooperationTable
          :municipality-cooperations="municipalityCooperations"
          @add-cooperation="handleAddMunicipalityCooperation"
          @open-detail="handleOpenMunicipalityCooperationDetail"
        />
        <RiskAssessmentHistoryTable
          :risk-assessment-histories="riskAssessmentHistories"
          @open-karte="handleOpenRiskAssessmentKarte"
        />
        <EpdsHistoryTable
          :epds-histories="epdsHistories"
          @add-epds="handleAddEpds"
          @view-detail="handleViewEpdsDetail"
        />
        <ConsultationServiceHistoryTable
          :consultation-service-histories="consultationServiceHistories"
          @open-karte="handleOpenConsultationServiceKarte"
        />
      </v-container>

      <ExternalLinkDetailWindow
        v-if="isExternalLinkDialogOpen"
        :external-link="activeExternalLink"
        :is-saving="isDetailSaving"
        :show-delete="!isSavingNewExternalLink"
        @cancel="closeExternalLinkDialog"
        @save="saveExternalLink"
        @delete="handleDeleteExternalLink"
      />
      <HighRiskAlertSupportDetailWindow
        v-if="isHighRiskAlertSupportDialogOpen"
        :alert-support="activeHighRiskAlertSupport"
        :is-saving="isDetailSaving"
        @cancel="closeHighRiskAlertSupportDialog"
        @save="saveHighRiskAlertSupport"
        @open-karte="handleOpenHighRiskAlertSupportKarte"
      />
      <FrequentSupportDetailWindow
        v-if="isFrequentSupportDialogOpen"
        :frequent-support="activeFrequentSupport"
        :is-saving="isDetailSaving"
        :show-delete="!isSavingNewFrequentSupport"
        @cancel="closeFrequentSupportDialog"
        @save="saveFrequentSupport"
        @delete="handleDeleteFrequentSupport"
      />
      <MunicipalityCooperationDetailWindow
        v-if="isMunicipalityCooperationDialogOpen"
        :cooperation="activeMunicipalityCooperation"
        :is-saving="isDetailSaving"
        :show-delete="!isSavingNewMunicipalityCooperation"
        @cancel="closeMunicipalityCooperationDialog"
        @save="saveMunicipalityCooperation"
        @delete="handleDeleteMunicipalityCooperation"
      />

      <v-dialog
        v-model="isRegistrationGuideDialogOpen"
        width="560"
      >
        <v-card>
          <v-card-title class="text-h6">
            {{ registrationGuideTitle }}
          </v-card-title>
          <v-card-text>
            {{ registrationGuideMessage }}
          </v-card-text>
          <v-card-actions class="justify-end">
            <v-btn
              color="#3ACAC9"
              variant="flat"
              @click="isRegistrationGuideDialogOpen = false"
            >
              閉じる
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="deleteConfirm.isOpen"
        width="480"
        :persistent="isDetailSaving"
        @update:model-value="(value) => { if (!value) cancelDelete(); }"
      >
        <v-card>
          <v-card-title class="text-h6">
            {{ deleteConfirm.label }}の削除
          </v-card-title>
          <v-card-text>
            この{{ deleteConfirm.label }}を削除します。削除すると元に戻せません。よろしいですか？
          </v-card-text>
          <v-card-actions class="justify-end">
            <v-btn
              variant="outlined"
              :disabled="isDetailSaving"
              @click="cancelDelete"
            >
              キャンセル
            </v-btn>
            <v-btn
              color="#d32f2f"
              variant="flat"
              :loading="isDetailSaving"
              :disabled="isDetailSaving"
              @click="confirmDelete"
            >
              削除する
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
    <v-snackbar
      v-model="conflictSnackbar"
      :timeout="6000"
      color="error"
      location="bottom right"
    >
      {{ conflictMessage }}
    </v-snackbar>
    <v-snackbar
      v-model="permissionDeniedSnackbar"
      :timeout="-1"
      color="error"
      location="top"
    >
      {{ permissionDeniedMessage }}
    </v-snackbar>
    <v-snackbar
      v-model="genericErrorSnackbar"
      :timeout="6000"
      color="error"
      location="bottom right"
    >
      {{ genericErrorMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import TopMenu from "@/vue/components/shared/TopMenu.vue";
import ChildrenInfoTable from "./ChildrenInfoTable.vue";
import ChildrenInfoDetailWindow from "./ChildrenInfoDetailWindow.vue";
import ConsultationServiceHistoryTable from "./ConsultationServiceHistoryTable.vue";
import DetailPageHeader from "./DetailPageHeader.vue";
import EpdsHistoryTable from "./EpdsHistoryTable.vue";
import EventTimelineTable from "./EventTimelineTable.vue";
import ExternalLinkDetailWindow from "./ExternalLinkDetailWindow.vue";
import ExternalLinksTable from "./ExternalLinksTable.vue";
import FrequentSupportDetailWindow from "./FrequentSupportDetailWindow.vue";
import FrequentSupportTable from "./FrequentSupportTable.vue";
import HighRiskAlertSupportDetailWindow from "./HighRiskAlertSupportDetailWindow.vue";
import HighRiskAlertSupportTable from "./HighRiskAlertSupportTable.vue";
import MunicipalityCooperationDetailWindow from "./MunicipalityCooperationDetailWindow.vue";
import MunicipalityCooperationTable from "./MunicipalityCooperationTable.vue";
import RiskAssessmentHistoryTable from "./RiskAssessmentHistoryTable.vue";
import UserInfoCard from "./UserInfoCard.vue";
import UserSpecialNoteSection from "./UserSpecialNoteSection.vue";
import { createFaceSheetsActions, GENERIC_ERROR_MESSAGE, isForbiddenError, OPTIMISTIC_LOCK_ALERT_MESSAGE, PERMISSION_DENIED_MESSAGE } from "@/vue/entities/face-sheet";
import { sessionTimeoutState } from "@/vue/features/session-timeout";

const LEAVE_WARNING_MESSAGE = "編集中のデータは破棄されますが、よろしいですか？";

const emptyUserProfile = {
  gender: "-",
  deliveryDate: "-",
  phoneNumber: "-",
  email: "-",
  couponId: "-",
  level2FrequentUseControlLabel: "",
  possiblyDummy: false,
  withdrawn: false,
  withdrawnAt: ""
};

export default {
  name: "FaceSheetsDetailPage",
  components: {
    ChildrenInfoTable,
    ChildrenInfoDetailWindow,
    ConsultationServiceHistoryTable,
    DetailPageHeader,
    EpdsHistoryTable,
    EventTimelineTable,
    ExternalLinkDetailWindow,
    ExternalLinksTable,
    FrequentSupportDetailWindow,
    FrequentSupportTable,
    HighRiskAlertSupportDetailWindow,
    HighRiskAlertSupportTable,
    MunicipalityCooperationDetailWindow,
    MunicipalityCooperationTable,
    RiskAssessmentHistoryTable,
    TopMenu,
    UserInfoCard,
    UserSpecialNoteSection
  },
  beforeRouteLeave(to, from, next) {
    this.confirmLeaveIfEditing(next);
  },
  beforeRouteUpdate(to, from, next) {
    this.confirmLeaveIfEditing(next);
  },
  emits: ["logout"],
  data() {
    return {
      faceSheetsActions: null,
      faceSheet: null,
      detailData: null,
      // 詳細データの取得中フラグ。ロード完了までは「見つかりません」ではなくローディング表示にする
      isLoading: true,
      conflictSnackbar: false,
      conflictMessage: OPTIMISTIC_LOCK_ALERT_MESSAGE,
      permissionDeniedSnackbar: false,
      permissionDeniedMessage: PERMISSION_DENIED_MESSAGE,
      genericErrorSnackbar: false,
      genericErrorMessage: GENERIC_ERROR_MESSAGE,
      activeChild: null,
      activeChildNoteHistory: [],
      activeExternalLink: null,
      activeFrequentSupport: null,
      activeHighRiskAlertSupport: null,
      activeMunicipalityCooperation: null,
      isChildDetailDialogOpen: false,
      isExternalLinkDialogOpen: false,
      isEditingSpecialNote: false,
      isFrequentSupportDialogOpen: false,
      isHighRiskAlertSupportDialogOpen: false,
      isMunicipalityCooperationDialogOpen: false,
      isRegistrationGuideDialogOpen: false,
      isSavingNewExternalLink: false,
      isSavingNewFrequentSupport: false,
      isSavingNewMunicipalityCooperation: false,
      // 詳細ダイアログの保存処理中フラグ。保存ボタン連打による重複登録を防ぐ。
      isDetailSaving: false,
      // イベント削除の確認ダイアログ。削除対象の種別・ID・表示名を保持する。
      deleteConfirm: {
        isOpen: false,
        type: null,
        id: null,
        label: ""
      },
      registrationGuideMessage: "",
      registrationGuideTitle: "",
      specialNote: "",
      specialNoteDraft: "",
      specialNoteBaseRevisionId: null
    };
  },
  computed: {
    userId() {
      return this.$route.params.user_id || "";
    },
    displayName() {
      return this.faceSheet?.nameKana || "該当ユーザーなし";
    },
    // ハイリスク要対応・要注意観察中は独立したフラグ（同時にONの場合、両方のバッジを表示する）。
    isRequiredStatus() {
      return this.faceSheet?.actionRequired === true;
    },
    isWatchStatus() {
      return this.faceSheet?.watching === true;
    },
    userProfile() {
      return this.detailData?.userProfile || emptyUserProfile;
    },
    userBirthdayWithAge() {
      return this.faceSheet?.userBirthdayWithAge || "-";
    },
    formattedPostalCode() {
      const raw = this.faceSheet?.postalCode;
      if (!raw) return "-";

      const [postalCode = "", address = ""] = raw.split("\n");
      const normalizedPostalCode = postalCode.replace("-", "");
      const formattedPostalCode = normalizedPostalCode.length === 7
        ? `${normalizedPostalCode.slice(0, 3)}-${normalizedPostalCode.slice(3)}`
        : postalCode;

      return `〒${formattedPostalCode} ${address}`.trim();
    },
    children() {
      return this.detailData?.children || [];
    },
    externalLinks() {
      return this.detailData?.externalLinks || [];
    },
    events() {
      return this.detailData?.events || [];
    },
    highRiskAlertSupports() {
      return this.detailData?.highRiskAlertSupports || [];
    },
    frequentSupports() {
      return this.detailData?.frequentSupports || [];
    },
    municipalityCooperations() {
      return this.detailData?.municipalityCooperations || [];
    },
    riskAssessmentHistories() {
      return this.detailData?.riskAssessmentHistories || [];
    },
    epdsHistories() {
      return this.detailData?.epdsHistories || [];
    },
    consultationServiceHistories() {
      return this.detailData?.consultationServiceHistories || [];
    },
    // 直近の相談カルテ = 相談サービス利用履歴（日時降順）の先頭のカルテURL。
    latestConsultationKarteUrl() {
      return this.consultationServiceHistories[0]?.karteUrl || "";
    },
    specialNoteHistory() {
      return this.detailData?.specialNoteHistory || [];
    },
    // 何らかの編集中データを持つか（特記の編集中、または各種登録/編集ダイアログが開いている状態）。
    // 子の情報ダイアログ(閲覧のみ)・登録案内ダイアログは編集を伴わないため対象外。
    hasUnsavedEdits() {
      return this.isEditingSpecialNote ||
        this.isExternalLinkDialogOpen ||
        this.isHighRiskAlertSupportDialogOpen ||
        this.isFrequentSupportDialogOpen ||
        this.isMunicipalityCooperationDialogOpen;
    }
  },
  watch: {
    userId() {
      this.loadDetail();
    }
  },
  created() {
    this.faceSheetsActions = createFaceSheetsActions(() => this.showConflictToast(), () => this.showGenericError());
    this.loadDetail();
  },
  mounted() {
    window.addEventListener("beforeunload", this.onBeforeUnload);
  },
  beforeUnmount() {
    window.removeEventListener("beforeunload", this.onBeforeUnload);
  },
  methods: {
    confirmLeaveIfEditing(next) {
      // 自動ログアウトを最優先する。タイムアウト発生時は編集中データの破棄確認を出さず、
      // 即座に離脱（＝ログアウト遷移）を許可する。
      if (sessionTimeoutState.timeout) {
        next();
        return;
      }
      if (!this.hasUnsavedEdits) {
        next();
        return;
      }
      const isConfirmed = window.confirm(LEAVE_WARNING_MESSAGE);
      if (isConfirmed) {
        next();
      } else {
        next(false);
      }
    },
    // 未保存の編集中にタブを閉じる/リロードする場合に、ブラウザ標準の確認ダイアログを出す。
    onBeforeUnload(event) {
      if (!event || !this.hasUnsavedEdits) return;
      // 自動ログアウト起因の場合は破棄確認を出さない。
      if (sessionTimeoutState.timeout) return;
      event.preventDefault();
      event.returnValue = "";
    },
    showConflictToast() {
      this.conflictSnackbar = true;
    },
    showGenericError() {
      this.genericErrorSnackbar = true;
    },
    async loadDetail() {
      // userId が変わって loadDetail が並行実行された場合に、古いリクエストの結果で
      // 状態を上書きしたり isLoading を誤って倒したりしないよう、開始時の userId を保持し、
      // 反映・後処理は現在の userId と一致する場合のみ行う。
      const requestedUserId = this.userId;
      this.isLoading = true;
      try {
        const result = await this.faceSheetsActions.fetchDetail(requestedUserId);
        if (requestedUserId !== this.userId) return;
        if (result) {
          this.applyDetailResult(result);
        } else {
          this.faceSheet = null;
          this.detailData = null;
        }
      } catch (error) {
        if (requestedUserId !== this.userId) return;
        this.faceSheet = null;
        this.detailData = null;
        if (isForbiddenError(error)) {
          this.redirectToTopWithoutPermission();
          return;
        }
      } finally {
        // 最新のリクエストのときだけローディングを解除する。
        if (requestedUserId === this.userId) {
          this.isLoading = false;
        }
      }
      this.resetSpecialNoteState();
    },
    redirectToTopWithoutPermission() {
      this.permissionDeniedSnackbar = true;
      window.setTimeout(() => {
        window.location.href = "/";
      }, 2000);
    },
    applyDetailResult({ item, detail }) {
      this.faceSheet = item;
      this.detailData = detail;
    },
    createLocalId(prefix) {
      return `${prefix}-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
    },
    resetSpecialNoteState() {
      this.specialNote = this.detailData?.specialNote || "";
      this.specialNoteDraft = this.specialNote;
      this.specialNoteBaseRevisionId = this.detailData?.specialNoteRevisionId || null;
      this.isEditingSpecialNote = false;
    },
    startEditSpecialNote() {
      this.specialNoteDraft = this.specialNote;
      this.isEditingSpecialNote = true;
    },
    async saveSpecialNote() {
      if (this.isDetailSaving) return;
      this.isDetailSaving = true;
      try {
        const result = await this.faceSheetsActions.saveSpecialNote(
          this.userId,
          this.specialNoteDraft,
          this.specialNoteBaseRevisionId
        );
        if (!result) return;
        this.applyDetailResult(result);
        this.specialNote = this.detailData?.specialNote || this.specialNoteDraft;
        this.specialNoteBaseRevisionId = this.detailData?.specialNoteRevisionId || null;
        this.isEditingSpecialNote = false;
      } finally {
        this.isDetailSaving = false;
      }
    },
    cancelEditSpecialNote() {
      this.specialNoteDraft = this.specialNote;
      this.isEditingSpecialNote = false;
    },
    async handleChangeStatus(statusFlags) {
      const item = await this.faceSheetsActions.updateStatus(this.userId, statusFlags, this.faceSheet.lockVersion);
      if (!item) return;
      this.faceSheet = item;
    },
    handleShowSpecialNoteHistory() {},
    handleOpenChildDetail(child) {
      this.activeChild = { ...child };
      this.activeChildNoteHistory = child.noteHistory || [];
      this.isChildDetailDialogOpen = true;
    },
    closeChildDetailDialog() {
      this.isChildDetailDialogOpen = false;
      this.activeChild = null;
      this.activeChildNoteHistory = [];
    },
    handleAddExternalLink() {
      this.isSavingNewExternalLink = true;
      this.activeExternalLink = {
        id: this.createLocalId("link"),
        titleType: null,
        titleText: "",
        registeredAt: "",
        url: ""
      };
      this.isExternalLinkDialogOpen = true;
    },
    handleOpenExternalLinkDetail(externalLink) {
      this.isSavingNewExternalLink = false;
      this.activeExternalLink = { ...externalLink };
      this.isExternalLinkDialogOpen = true;
    },
    closeExternalLinkDialog() {
      this.isExternalLinkDialogOpen = false;
      this.activeExternalLink = null;
      this.isSavingNewExternalLink = false;
    },
    async saveExternalLink(externalLink) {
      if (this.isDetailSaving) return;
      this.isDetailSaving = true;
      try {
        const result = await this.faceSheetsActions.saveExternalLink(this.userId, externalLink, this.isSavingNewExternalLink);
        if (!result) return;
        this.applyDetailResult(result);
        this.closeExternalLinkDialog();
      } finally {
        this.isDetailSaving = false;
      }
    },
    handleAddEvent() {
      this.registrationGuideTitle = "イベント時系列の新規登録";
      this.registrationGuideMessage = "イベント時系列の新規登録は、下部の『頻回対応』または『自治体連携』の新規登録モーダルから操作してください。";
      this.isRegistrationGuideDialogOpen = true;
    },
    // イベント時系列の各行のアクションボタン。対応するセクションの一覧から同じ id のレコードを探し、
    // そのセクションの「詳細を開く」/「相談カルテを開く」/「閲覧する」と同じ処理を呼ぶ。
    handleOpenTimelineEventDetail(event) {
      switch (event.sourceType) {
      case "alert": {
        const item = this.highRiskAlertSupports.find((candidate) => candidate.id === event.sourceId);
        if (item) this.handleOpenHighRiskAlertSupportDetail(item);
        return;
      }
      case "frequent_support": {
        const item = this.frequentSupports.find((candidate) => candidate.id === event.sourceId);
        if (item) this.handleOpenFrequentSupportDetail(item);
        return;
      }
      case "municipal_cooperation": {
        const item = this.municipalityCooperations.find((candidate) => candidate.id === event.sourceId);
        if (item) this.handleOpenMunicipalityCooperationDetail(item);
        return;
      }
      case "risk_assessment": {
        const item = this.riskAssessmentHistories.find((candidate) => candidate.id === event.sourceId);
        this.handleOpenRiskAssessmentKarte(item);
        return;
      }
      case "epds": {
        const item = this.epdsHistories.find((candidate) => candidate.id === event.sourceId);
        this.handleViewEpdsDetail(item);
        return;
      }
      case "consultation_service": {
        const item = this.consultationServiceHistories.find((candidate) => candidate.id === event.sourceId);
        this.handleOpenConsultationServiceKarte(item);
        return;
      }
      default:
      }
    },
    handleOpenHighRiskAlertSupportDetail(alertSupport) {
      this.activeHighRiskAlertSupport = { ...alertSupport };
      this.isHighRiskAlertSupportDialogOpen = true;
    },
    closeHighRiskAlertSupportDialog() {
      this.isHighRiskAlertSupportDialogOpen = false;
      this.activeHighRiskAlertSupport = null;
    },
    async saveHighRiskAlertSupport(alertSupport) {
      if (this.isDetailSaving) return;
      this.isDetailSaving = true;
      try {
        const result = await this.faceSheetsActions.saveHighRiskAlertSupport(this.userId, alertSupport);
        if (!result) return;
        this.applyDetailResult(result);
        this.closeHighRiskAlertSupportDialog();
      } finally {
        this.isDetailSaving = false;
      }
    },
    handleAddFrequentSupport() {
      this.isSavingNewFrequentSupport = true;
      this.activeFrequentSupport = {
        id: this.createLocalId("frequent-support"),
        supportedAt: "",
        supportType: null,
        note: ""
      };
      this.isFrequentSupportDialogOpen = true;
    },
    handleOpenFrequentSupportDetail(frequentSupport) {
      this.isSavingNewFrequentSupport = false;
      this.activeFrequentSupport = { ...frequentSupport };
      this.isFrequentSupportDialogOpen = true;
    },
    closeFrequentSupportDialog() {
      this.isFrequentSupportDialogOpen = false;
      this.activeFrequentSupport = null;
      this.isSavingNewFrequentSupport = false;
    },
    async saveFrequentSupport(frequentSupport) {
      if (this.isDetailSaving) return;
      this.isDetailSaving = true;
      try {
        const result = await this.faceSheetsActions.saveFrequentSupport(this.userId, frequentSupport, this.isSavingNewFrequentSupport);
        if (!result) return;
        this.applyDetailResult(result);
        this.closeFrequentSupportDialog();
      } finally {
        this.isDetailSaving = false;
      }
    },
    handleAddMunicipalityCooperation() {
      this.isSavingNewMunicipalityCooperation = true;
      this.activeMunicipalityCooperation = {
        id: this.createLocalId("municipality-cooperation"),
        consentMailRequestedAt: "",
        consentMailSentAt: "",
        replied: null,
        repliedAt: "",
        replyContent: null,
        cooperated: null,
        partnerMunicipality: false,
        partnerChildGuidanceCenter: false,
        partnerPolice: false,
        partnerHospital: false,
        partnerOther: false,
        municipalityName: "",
        municipalityTel: "",
        municipalityEmail: "",
        municipalityStaffName: "",
        cooperatedAt: "",
        individualIdentified: null,
        supportStatus: null,
        municipalityReaction: "",
        note: ""
      };
      this.isMunicipalityCooperationDialogOpen = true;
    },
    handleOpenMunicipalityCooperationDetail(cooperation) {
      this.isSavingNewMunicipalityCooperation = false;
      this.activeMunicipalityCooperation = { ...cooperation };
      this.isMunicipalityCooperationDialogOpen = true;
    },
    closeMunicipalityCooperationDialog() {
      this.isMunicipalityCooperationDialogOpen = false;
      this.activeMunicipalityCooperation = null;
      this.isSavingNewMunicipalityCooperation = false;
    },
    async saveMunicipalityCooperation(cooperation) {
      if (this.isDetailSaving) return;
      this.isDetailSaving = true;
      try {
        const result = await this.faceSheetsActions.saveMunicipalityCooperation(this.userId, cooperation, this.isSavingNewMunicipalityCooperation);
        if (!result) return;
        this.applyDetailResult(result);
        this.closeMunicipalityCooperationDialog();
      } finally {
        this.isDetailSaving = false;
      }
    },
    // 各詳細ダイアログのゴミ箱アイコンから呼ばれる。削除確認ダイアログを開くだけで、
    // 実際の削除は confirmDelete で行う。type ごとに confirmDelete で呼ぶアクションを切り替える。
    handleDeleteExternalLink() {
      this.openDeleteConfirm("externalLink", this.activeExternalLink?.id, "外部リンク");
    },
    handleDeleteFrequentSupport() {
      this.openDeleteConfirm("frequentSupport", this.activeFrequentSupport?.id, "頻回対応");
    },
    handleDeleteMunicipalityCooperation() {
      this.openDeleteConfirm("municipalityCooperation", this.activeMunicipalityCooperation?.id, "自治体連携");
    },
    openDeleteConfirm(type, id, label) {
      if (!id) return;
      this.deleteConfirm = { isOpen: true, type, id: String(id), label };
    },
    cancelDelete() {
      if (this.isDetailSaving) return;
      // 閉じるアニメーション中に共通文言がチラつかないよう、label 等は保持したまま isOpen だけ倒す。
      // 各フィールドは次回 openDeleteConfirm で上書きされる。
      this.deleteConfirm.isOpen = false;
    },
    async confirmDelete() {
      if (this.isDetailSaving) return;
      const { type, id } = this.deleteConfirm;
      if (!type || !id) return;

      this.isDetailSaving = true;
      try {
        let result = null;
        let closeDialog = null;
        switch (type) {
        case "externalLink":
          result = await this.faceSheetsActions.deleteExternalLink(this.userId, id);
          closeDialog = this.closeExternalLinkDialog;
          break;
        case "frequentSupport":
          result = await this.faceSheetsActions.deleteFrequentSupport(this.userId, id);
          closeDialog = this.closeFrequentSupportDialog;
          break;
        case "municipalityCooperation":
          result = await this.faceSheetsActions.deleteMunicipalityCooperation(this.userId, id);
          closeDialog = this.closeMunicipalityCooperationDialog;
          break;
        default:
          return;
        }
        if (!result) return;
        this.applyDetailResult(result);
        // 閉じるアニメーション中の文言チラつき防止のため label 等は保持し、isOpen だけ倒す。
        this.deleteConfirm.isOpen = false;
        if (closeDialog) closeDialog();
      } finally {
        this.isDetailSaving = false;
      }
    },
    handleOpenRiskAssessmentKarte(riskAssessment) {
      this.openKarteInNewTab(riskAssessment?.karteUrl);
    },
    handleOpenHighRiskAlertSupportKarte(alertSupport) {
      this.openKarteInNewTab(alertSupport?.karteUrl);
    },
    handleAddEpds() {
      this.registrationGuideTitle = "EPDS履歴の新規登録";
      this.registrationGuideMessage = "EPDS履歴の新規登録は準備中です。現在は『閲覧する』から既存履歴の確認のみ可能です。";
      this.isRegistrationGuideDialogOpen = true;
    },
    // EPDS詳細のポップアップ表示。実データ（/epds/:id、EpdsView.vue）を開く。
    handleViewEpdsDetail(epdsHistory) {
      const epdsId = epdsHistory?.id;
      if (!epdsId) return;
      window.open(`/epds/${epdsId}`, "", "width=480, height=640");
    },
    handleOpenConsultationServiceKarte(consultationHistory) {
      this.openKarteInNewTab(consultationHistory?.karteUrl);
    },
    handleOpenLatestKarte() {
      this.openKarteInNewTab(this.latestConsultationKarteUrl);
    },
    // 紐づく相談カルテを新しいタブで開く。URLが無い場合は何もしない。
    openKarteInNewTab(url) {
      if (!url) return;
      window.open(url, "_blank", "noopener");
    }
  }
};
</script>

<style lang="scss" scoped>
.detail-table-list {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin: 24px auto;
  width: calc(100% - 48px);
}

.face-sheet-loading {
  align-items: center;
  display: flex;
  gap: 12px;
}

.face-sheet-loading__text {
  color: rgba(0, 0, 0, 0.6);
  font-size: 14px;
}
</style>
